//=============================================================================
//                       <--- Developed By Icon Team ---> 18 January 2021
//=============================================================================

// npm packages
import React, { useState, useEffect, useContext, useCallback } from "react";
import context from "context/context";
import { toast } from "react-toastify";
import { useHistory } from "react-router-dom";
import * as moment from "moment";
import { CSVLink } from "react-csv";
import { useTranslation } from 'react-i18next';

// custom pages
import Table from "./Table/Table";
import ReusableTable from "components/Table/Table";
import maxMerch from "apis/maxmerch/maxmerch";
import tierAssignment from "apis/tier-assignment/tier-assignment";
import useDebounce from "helpers/useDebounce";
import { styles } from "./styles";
import Loader from "components/Loader/Loader";
import searchIcon from "assets/img/Search_Icon.png";
import { exportHeaders } from "./export";
import audit from "apis/audit/audit";

// material-ui
import {
  MuiPickersUtilsProvider,
  KeyboardDatePicker,
} from "@material-ui/pickers";
import { withStyles } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import InputAdornment from "@material-ui/core/InputAdornment";
import TextField from "@material-ui/core/TextField";
import MenuItem from "@material-ui/core/MenuItem";
import Card from "@material-ui/core/Card";
import Badge from "@material-ui/core/Badge";
import Checkbox from "@material-ui/core/Checkbox";
import DateFnsUtils from "@date-io/date-fns";
import Dialog from "@material-ui/core/Dialog";

// material-ui icons
import FilterListIcon from "@material-ui/icons/FilterList";
import Grid from "@material-ui/core/Grid";
import access from "config/access";
import CloseOutlinedIcon from "@material-ui/icons/CloseOutlined";
import SearchOutlinedIcon from "@material-ui/icons/SearchOutlined";
import EditIcon from "@material-ui/icons/Edit";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import HighlightOffIcon from "@material-ui/icons/HighlightOff";
import HistoryIcon from "@material-ui/icons/History";
import { blockInvalidChar } from "helpers/blockInvalidChar";


//Helpers
import {dateFormat, pointsFormat} from "helpers/dataFormat"
import { enUS, fr } from "date-fns/locale";

// custom TextField
const CssTextField = withStyles({
  root: {
    "& label.Mui-focused": {
      color: "##D3D3D3",
    },
    "& .MuiFormLabel-root": {
      color: "#707070",
    },
    "& .MuiInputBase-input": {
      color: "#707070",
    },
    "& .MuiInput-underline:before": {
      borderBottomColor: "#707070!important",
    },
    "& .MuiInput-underline:after": {
      borderBottomColor: "#707070!important",
    },
    "& .MuiOutlinedInput-root": {
      "& fieldset": {
        borderColor: "#707070",
      },
      "&:hover fieldset": {
        borderColor: "#707070",
      },
      "&.Mui-focused fieldset": {
        borderColor: "#707070",
      },
      "&.MuiOutlinedInput-root": {
        borderRadius: 20,
      },
      "&.MuiOutlinedInput-root": {
        marginTop: 0,
        borderRadius: 20,
      },
    },
  },
})(TextField);

export default function TierAssignment(props) {
  const { state, dispatch } = useContext(context);
  const { getTier } = maxMerch();
  const { get, search, filter, exportDataAPI, getRunDate, updateRunDate } =
    tierAssignment();
  const { getAudit } = audit();

  const classes = styles();

  const [data, setData] = useState([]);
  const [offset, setOffset] = useState(1);
  const [count, setCount] = useState(0);
  const [filterCard, setFilterCard] = useState(false);
  const [searchValue, setSearchValue] = useState(null);
  const searchBounceValues = useDebounce(searchValue);
  const [exportData, setExportData] = useState([]);
  const [runDate, setRunDate] = useState(null);
  const [runDateValue, setRunDateValue] = useState(null);
  const [dialog, setDialog] = useState(false);
  const { t, i18n } = useTranslation()
  const [states, setstates] = useState({
    tier: [],
    filter: false,
    filterTier: "",
    difference: "",
    dollarDifference: "",
    startDate: null,
    endDate: null,
    auditLogs: [],
    historyDialog: false,
    historyData: [],
    historyLoader: false,
  });

  const setStates = useCallback((newState) => {
    setstates((states) => ({ ...states, ...newState }));
  }, []);

  // For bread-crumbs
  useEffect(() => {
    dispatch({
      type: "SET_PATHNAME",
      payload: ["HOME", "ADMIN", "Tier Assignment"],
    });
  }, []);

  // handling api calls
  useEffect(() => {
    states.filter
      ? handleFilter()
      : searchValue && searchValue.length > 0
        ? handleSearch()
        : handleGet();
  }, [offset]);

  // handling search api
  useEffect(() => {
    if (searchBounceValues && searchBounceValues.length > 0) {
      handleSearch();
    } else {
      setOffset(1);
    }
  }, [searchBounceValues]);

  // change pagination func
  const handlePagination = (value) => {
    setOffset(value);
  };

  // roles api func
  useEffect(() => {
    handleGetData();
  }, []);

  // tier api func
  const handleGetData = async () => {
    try {
      let res = await getTier();
      if (res) {
        setStates({ tier: res.data });
      }
      let date = await getRunDate();
      if (date) {
        setRunDate(date?.data?.tier_execution_date);
      }
    } catch (e) {
      console.log({ e });
    }
  };

  // authorized members api func
  const handleGet = async () => {
    try {
      let values;
      values = {
        offset: (offset - 1) * 10,
        limit: 10,
      };
      dispatch({ type: "SET_LOADING", payload: true });
      let res = await get(values);
      if (res) {
        setData(res.data);
        setCount(res.count);
        dispatch({ type: "SET_LOADING", payload: false });
      }
    } catch (e) {
      console.log({ e });
    }
  };

  // search api func
  const handleSearch = async () => {
    setStates({
      filter: false,
      filterTier: "",
      difference: "",
      dollarDifference: "",
      startDate: null,
      endDate: null,
    });
    try {
      let values;

      if (!searchValue.replace(/\s/g, "").length) {
        return;
      }
      values = {
        search: searchValue,
        offset: (offset - 1) * 10,
        limit: 10,
      };
      dispatch({ type: "SET_LOADING", payload: true });
      let res = await search(values);
      if (res) {
        setData(res.data);
        setCount(res.count);
        dispatch({ type: "SET_LOADING", payload: false });
        return;
      }
      dispatch({ type: "SET_LOADING", payload: false });
      setData([]);
    } catch (e) {
      console.log({ e });
    }
  };

  // filter api func
  const handleFilter = async () => {
    try {
      let values;

      if (
        !states.filterTier &&
        !states.difference &&
        !states.dollarDifference &&
        !states.startDate &&
        !states.endDate
      ) {
        return;
      }
      values = {
        offset: (offset - 1) * 10,
        limit: 10,
      };
      if (states.filterTier) {
        values = { ...values, tier: states.filterTier };
      }
      if (states.difference) {
        values = { ...values, difference: states.difference };
      }
      if (states.dollarDifference) {
        values = { ...values, dollarDifference: states.dollarDifference };
      }
      if (states.startDate && states.endDate) {
        values = {
          ...values,
          startDate: moment(states.startDate).format("MM/DD/YYYY"),
          endDate: moment(states.endDate).format("MM/DD/YYYY"),
        };
      }
      //console.log({ values });
      dispatch({ type: "SET_LOADING", payload: true });
      let res = await filter(values);
      if (res) {
        setData(res.data);
        setCount(res.count);
        setFilterCard(false);
        setStates({ filter: true });
        dispatch({ type: "SET_LOADING", payload: false });
      } else {
        setFilterCard(false);
      }
    } catch (e) {
      console.log({ e });
    }
  };

  // export api function
  const handleExport = async () => {
    let values = {};

    if (searchValue) {
      values = { ...values, search: searchValue };
    }

    if (states.filterTier) {
      values = { ...values, tier: states.filterTier };
    }

    if (states.difference) {
      values = { ...values, difference: states.difference };
    }

    if (states.dollarDifference) {
      values = { ...values, dollarDifference: states.dollarDifference };
    }

    if (states.startDate && states.endDate) {
      values = {
        ...values,
        startDate: moment(states.startDate).format("MM/DD/YYYY"),
        endDate: moment(states.endDate).format("MM/DD/YYYY"),
      };
    }

    let res = await exportDataAPI(values);

    if (res && res.data) {
      setExportData(res.data);

      if (res.data.length === 0) {
        toast.success(t("No Data Found"), { toastId: "no-report-date" });
        return false;
      }
      return true;
    }
  };

  // authorized members api func
  const handleUpdateRunDate = async (arg) => {
    try {
      let values;
      values = {
        runDate: runDateValue,
      };
      if (arg) {
        values = { ...values, ...arg };
      }
      let res = await updateRunDate(values);
      if (res) {
        setDialog(false);
        toast.success("Updated", { toastId: "run-date-updated" });
        setRunDate(runDateValue);
        setRunDateValue(null);
      }
      if (arg) {
        setStates({
          filter: false,
          filterTier: "",
          difference: "",
          dollarDifference: "",
          startDate: null,
          endDate: null,
        });
        setSearchValue("");
        if (offset == 1) {
          handleGet();
        } else {
          setOffset(1);
        }
      }
    } catch (e) {
      console.log({ e });
    }
  };

  const handleGetHistoryData = async (id) => {
    try {
      let values;
      values = {
        recordId: id,
      };
      let res = await getAudit(values);
      //console.log({ res });
      if (res) {
        setStates({ historyData: res?.data, historyLoader: false });
      }
    } catch (e) {
      console.log({ e });
    }
  };

  const pointFormat = (point) => {
    var nfObject = new Intl.NumberFormat("en-US");
    var res = nfObject.format(point);
    return res;
  };

  const locale = i18n.language == 'fr' ? fr : enUS;
  return (
    <React.Fragment>
      <Grid container className={classes.root}>
        <Grid xs={12} sm={12} lg={12}>
          <CssTextField
            id="loyalty-id"
            placeholder={t('Search for Loyalty Id')}
            type={"number"}
              onWheel={(e) => e.target.blur()}
            variant="outlined"
            onKeyDown={blockInvalidChar}
            autoComplete="off"
            className={classes.searchInput}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  {searchValue && searchValue.length > 0 ? (
                    <CloseOutlinedIcon
                      className={classes.searchInputIcon}
                      onClick={() => {
                        setSearchValue("");
                        setOffset(1);
                        handleGet();
                      }}
                    />
                  ) : (
                    <img
                      src={searchIcon}
                      alt=""
                      width="20px"
                      className={classes.searchInputIcon}
                    />
                  )}
                </InputAdornment>
              ),
            }}
            value={searchValue}
            onChange={(e) => {
              setSearchValue(e.target.value);
              if (e.target.value.length == 0) {
                handleGet();
              }
            }}
            onClick={() => {
              setOffset(1);
            }}
          />
          <Button
            className={`${classes.filterBtn} dash-1-filter-btn`}
            onClick={() => {
              setFilterCard(!filterCard);
              if (!states.filter) {
                setStates({ is_active: "", filterRole: "" });
              }
            }}
          >
            {t('Filter')}
            {states.filter && (
              <Badge
                badgeContent={count == 0 ? "" : count}
                classes={{ badge: classes.badge }}
                max={999}
              ></Badge>
            )}
          </Button>
          <Button
            className={`${classes.filterBtn} dash-1-filter-btn`}
            // disabled={!access['editAccess'][state.userInfo?.role]['tier-assignment']}
            onClick={()=>{
              if(state?.userInfo?.editAction){
              setDialog(true);
              return;
            }
          else{
            toast.error(state.userInfo?.editAccess)
          return;
        }}}
          >
            {t('Set Run Date')}
          </Button>
          {/* <span style={{ padding: "0px 10px" }}>
            <strong>Run Date</strong> :{" "}
            {runDate ? moment(runDate).format("MM/DD/YYYY") : "-"}
          </span> */}
          <CSVLink
            id="csv"
            data={exportData}
            headers={exportHeaders.map(head => {
              return { ...head, label: t(head.label) }
            })}
            filename={
              t(`Tier Assignment`) +
              " " +
              moment(new Date()).format("MM-DD-YYYY,h:mm A") +
              ".csv"
            }
            className={classes.CSVlink}
          />
          <Button
            className={`${classes.exportBtn} dash-1-filter-btn`}
            onClick={async () => {
              let res = await handleExport();
              if (res) {
                document.getElementById("csv").click();
              }
            }}
          >
            {t('Export')}
          </Button>
        </Grid>
      </Grid>

      {filterCard && (
        <div className={classes.filterCardDiv}>
          <Grid container spacing={0}>
            <Grid item xs={12} style={{ float: "right" }}>
              <Card className={classes.filterCard}>
                <Grid
                  container
                  spacing={0}
                  className={classes.filterCardGridContainer}
                >
                  <Grid
                    item
                    xs={2}
                    className={classes.filterCardGrid}
                    style={{ padding: "5px 10px 10px 15px" }}
                  >
                    <TextField
                      id={"tier"}
                      label={t("Audit Tier")}
                      select
                      fullWidth
                      type={"text"}
                      value={states.filterTier}
                      onChange={(e) => {
                        setStates({ filterTier: e.target.value });
                      }}
                    >
                      {states.tier.length > 0 &&
                        states.tier.map((k, i) => (
                          <MenuItem value={k.tier} key={i}>
                            {t(k.tier)}
                          </MenuItem>
                        ))}
                    </TextField>
                  </Grid>
                  <Grid
                    item
                    xs={2}
                    className={classes.filterCardGrid}
                    style={{ padding: "5px 10px 10px 15px" }}
                  >
                    <TextField
                      id={"Difference"}
                      label={t("Tier Difference")}
                      select
                      fullWidth
                      type={"text"}
                      value={states.difference}
                      onChange={(e) => {
                        setStates({ difference: e.target.value });
                      }}
                    >
                      {["Yes", "No"].map((k, i) => (
                        <MenuItem value={k} key={i}>
                          {t(k)}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid
                    item
                    xs={2}
                    className={classes.filterCardGrid}
                    style={{ padding: "5px 10px 10px 15px" }}
                  >
                    <TextField
                      id={"Difference"}
                      label={t("$ Difference")}
                      select
                      fullWidth
                      type={"text"}
                      value={states.dollarDifference}
                      onChange={(e) => {
                        setStates({ dollarDifference: e.target.value });
                      }}
                    >
                      {["Yes", "No"].map((tier, i) => (
                        <MenuItem value={tier} key={i}>
                          {t(tier)}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid
                    item
                    xs={2}
                    className={classes.filterCardGrid}
                    style={{ padding: "5px 10px 10px 10px" }}
                  >
                    <MuiPickersUtilsProvider utils={DateFnsUtils} locale={locale}>
                      <KeyboardDatePicker
                        disableToolbar
                        variant="inline"
                        format={i18n.language == "en" ? "MM/dd/yyyy" : "dd/MM/yyyy"}
                        margin="dense"
                        label={t("Enroll From")}
                        id="date-picker-inline"
                        className={classes.dateInput}
                        value={states.startDate}
                        maxDate={new Date()}
                        onChange={(date) => {
                          //console.log({ s: date });
                          if (
                            moment(moment(date).format("MM/DD/YYYY")).isAfter(
                              moment(states.endDate).format("MM/DD/YYYY")
                            )
                          ) {
                            toast.error(
                              "Start date should be greater than End date.",
                              {
                                toastId: "start-date-end-date",
                              }
                            );
                            setStates({ startDate: null });
                            return;
                          }
                          setStates({ startDate: date, dateApplied: true });
                        }}
                        KeyboardButtonProps={{
                          "aria-label": "change date",
                        }}
                        autoOk
                      />
                    </MuiPickersUtilsProvider>
                  </Grid>
                  <Grid
                    item
                    xs={2}
                    className={classes.filterCardGrid}
                    style={{ padding: "5px 10px 10px 10px" }}
                  >
                    <MuiPickersUtilsProvider utils={DateFnsUtils} locale={locale}>
                      <KeyboardDatePicker
                        disableToolbar
                        variant="inline"
                        format={i18n.language == "en" ? "MM/dd/yyyy" : "dd/MM/yyyy"}
                        margin="dense"
                        label={t("Enroll To")}
                        id={t('date-picker-inline2')}
                        className={classes.dateInput}
                        value={states.endDate}
                        maxDate={new Date()}
                        onChange={(date) => {
                          console.log({ e: date });
                          if (
                            moment(moment(date).format("MM/DD/YYYY")).isBefore(
                              moment(states.startDate).format("MM/DD/YYYY")
                            )
                          ) {
                            toast.error(
                              "End date should be greater than Start date.",
                              {
                                toastId: "start-date-end-date",
                              }
                            );
                            setStates({ endDate: null });
                            return;
                          } else if (
                            moment(moment(date).format("MM/DD/YYYY")).isAfter(
                              moment(new Date()).format("MM/DD/YYYY")
                            )
                          ) {
                            setStates({ endDate: null });
                            toast.error(
                              "End date should be lesser that future date.",
                              {
                                toastId: "start-date-end-date",
                              }
                            );
                            return;
                          }
                          setStates({ endDate: date, dateApplied: true });
                        }}
                        KeyboardButtonProps={{
                          "aria-label": "change date",
                        }}
                        autoOk
                      />
                    </MuiPickersUtilsProvider>
                  </Grid>
                  <Grid
                    item
                    xs={1}
                    className={classes.filterCardBtnGrid}
                    style={{ padding: 10 }}
                  >
                    <Button
                      className={classes.filterCardApplyBtn}
                      style={{
                        fontSize: 15,
                        height: 35,
                        marginTop: 0,
                      }}
                      onClick={() => {
                        if (
                          !states?.filterTier &&
                          !states?.difference &&
                          !states?.dollarDifference &&
                          !states?.startDate &&
                          !states?.endDate
                        ) {
                          toast.error("Please select any field.", {
                            toastId: "filter-error",
                          });
                          return;
                        } else if (
                          (states?.startDate && !states?.endDate) ||
                          (!states?.startDate && states?.endDate)
                        ) {
                          toast.error("Please fill date range.", {
                            toastId: "filter-error-2",
                          });
                          return;
                        }
                        setSearchValue("");
                        setStates({ filter: true });
                        setOffset(1);
                        handleFilter();
                      }}
                    >
                      {t('Apply')}
                    </Button>
                    {states.filter && (
                      <Button
                        className={classes.filterCardResetBtn}
                        onClick={() => {
                          setStates({
                            filter: false,
                            filterTier: "",
                            difference: "",
                            dollarDifference: "",
                            startDate: null,
                            endDate: null,
                          });
                          setOffset(1);
                          setFilterCard(false);
                          handleGet();
                        }}
                      >
                        {t('Reset')}
                      </Button>
                    )}
                  </Grid>
                </Grid>
              </Card>
            </Grid>
          </Grid>
        </div>
      )}

      <Loader Loading={state.loading} top={"15%"}>
        <Grid className={classes.tableDownInResponsive}>
          <Grid xs={12} sm={12} lg={12}>
            <Table
              headings={[
                "Loyalty ID",
                "Initial CS",
                "Initial OS",
                "Initial Total",
                "Initial Tier",
                "Enrollment Date",
                "Audit Tier",
                "Annex Tier",
                "Annex $",
                "Tier Diff",
                "$ Diff",
                "Tier Start Date",
                "Current OS",
                "Current CS",
                "Current Total",
                "Current Tier",
                "Current Run Date",
                "",
              ]}
              data={
                data &&
                data.length > 0 &&
                data.map((d, i) => [
                  d?.loyalty_id,
                  <div className={classes.beforeDiv}>
                    {d?.credit_sales_summary_amount_before_enroll}
                  </div>,
                  d?.order_summary_amount_before_enroll,
                  t(d?.initial_points),
                  t(d?.initial_tier),
                  d?.["Loyalty_Account.created_at"]
                    ? dateFormat(d?.["Loyalty_Account.created_at"], i18n.language)
                    : "-",
                  t(d?.overall_tier),
                  t(d?.["Loyalty_Account.sales_summary_tier"]),
                  d?.["Loyalty_Account.current_tier_points"]
                    ? pointsFormat(d?.["Loyalty_Account.current_tier_points"], i18n.language)
                    : "-",
                  t(d?.tier_difference),
                  t(d?.dollar_difference),
                  d?.["Loyalty_Account.tier_start_date"]
                    ? dateFormat(d?.["Loyalty_Account.tier_start_date"], i18n.language)
                    : "-",
                  d?.sales_transaction_amount_after_enroll,
                  d?.credit_sales_amount_after_enroll,
                  d?.total_points,
                  t(d?.bumpup_tier),
                  d?.run_date ? dateFormat(d?.run_date, i18n.language) : "-",
                  // <HistoryIcon
                  //   style={{ cursor: "pointer" }}
                  //   onClick={() => {
                  //     setStates({ historyDialog: true, historyLoader: true });
                  //     handleGetHistoryData(d?.id);
                  //   }}
                  // />,
                ])
              }
              offset={offset}
              count={
                (count / 10) % 1 == 0
                  ? Math.floor(count / 10)
                  : Math.floor(count / 10 + 1)
              }
              handlePagination={handlePagination}
            />
          </Grid>
        </Grid>
      </Loader>
      {/*Dialog */}
      <Dialog
        onClose={() => {
          setDialog(false);
        }}
        aria-labelledby="simple-dialog-title"
        open={dialog}
        style={{ fontFamily: "Segoe-ui" }}
      >
        <div className={classes.dialogParentDiv}>
          <div className={classes.parentText}>{t('Set Run Date')}</div>

          <div className={classes.runDateInput}>
            <MuiPickersUtilsProvider utils={DateFnsUtils} locale={locale}>
              <KeyboardDatePicker
                disableToolbar
                variant="inline"
                format={i18n.language == "en" ? "MM/dd/yyyy" : "dd/MM/yyyy"}
                margin="dense"
                label={t("Date")}
                id="date-picker-inline"
                className={classes.dateInput}
                value={runDateValue}
                maxDate={new Date()}
                onChange={(date) => {
                  //console.log({ s: date });
                  if (
                    moment(moment(date).format("MM/DD/YYYY")).isSame(
                      moment(runDate).format("MM/DD/YYYY")
                    )
                  ) {
                    toast.error("Please select another date", {
                      toastId: "start-date-end-date",
                    });
                    return;
                  } else if (date) {
                    setRunDateValue(moment(date).format("MM/DD/YYYY"));
                  }
                }}
                KeyboardButtonProps={{
                  "aria-label": "change date",
                }}
                autoOk
              />
            </MuiPickersUtilsProvider>
          </div>
          <Grid container spacing={0}>
            <Grid item xs={6}>
              <Button
                className={classes.cancelBtn}
                onClick={() => {
                  setDialog(false);
                }}
              >
                {t('Cancel')}
              </Button>
            </Grid>
            <Grid item xs={6} className={classes.proceedBtnGrid}>
              {/* <Button
                className={classes.proceedBtn}
                onClick={() => {
                  if (runDateValue) {
                    handleUpdateRunDate();
                  } else {
                    toast.error("Please select an date.", {
                      toastId: "run-date-error",
                    });
                  }
                }}
              >
                Update
              </Button> */}
              <Button
                className={classes.procedureBtn}
                onClick={() => {
                  if (runDateValue) {
                    handleUpdateRunDate({ procedure: true });
                  } else {
                    toast.error("Please select an date.", {
                      toastId: "run-date-error",
                    });
                  }
                }}
              >
                {t('Execute')}
              </Button>
            </Grid>
            {/* <Grid item xs={12} className={classes.procedureBtnGrid}></Grid> */}
          </Grid>
        </div>
      </Dialog>
      <Dialog
        onClose={() => {
          setStates({ historyDialog: false, historyData: [] });
        }}
        aria-labelledby="simple-dialog-title"
        open={states.historyDialog}
        style={{ fontFamily: "Segoe-ui" }}
        maxWidth={false}
      >
        <div className={classes.dialogParentDiv}>
          <div className={classes.parentText} style={{ paddingBottom: 10 }}>
            {t('History')}{" "}
            <span style={{ float: "right" }}>
              <HighlightOffIcon
                style={{ verticalAlign: "middle", cursor: "pointer" }}
                onClick={() => {
                  setStates({ historyDialog: false, historyData: [] });
                }}
              />
            </span>
          </div>

          {!states.historyLoader && (
            <ReusableTable
              headings={[
                "Column Name",
                "Old Value",
                "New Value",
                "Modified At",
                "Type",
                "Modified By",
              ]}
              data={
                states?.historyData?.length > 0 &&
                states?.historyData.map((d, i) => [
                  d?.column_name,
                  d?.old_value,
                  d?.new_value,
                  d?.created_at ? moment(d?.created_at).format("LLLL") : "",
                  d?.altered_by,
                  `${d?.["User.first_name"] ? d?.["User.first_name"] : ""} ${d?.["User.last_name"] ? d?.["User.last_name"] : ""
                  }`,
                ])
              }
              disablePagination={true}
            />
          )}
        </div>
      </Dialog>
    </React.Fragment>
  );
}
